import { lazy, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { MainLayout } from '@/components/layout/main-layout';
import { AdminLayout } from '@/components/layout/admin-layout';
import { AuthProvider } from '@/hooks/useAuth';
import { AdminAuthProvider } from '@/hooks/useAdminAuth';
import { ErrorBoundary } from '@/components/common/error-boundary';
import { ToastContainer } from '@/ui/toast';
import { TooltipProvider } from '@/ui/tooltip';
import '@/styles/globals.css';

// 路由级懒加载 - 前台页面
const ResumeManagementPage = lazy(() =>
  import('@/features/resume/pages/resume-management').then((m) => ({
    default: m.ResumeManagementPage,
  }))
);
const JobProfilePage = lazy(() => import('@/features/job/pages/job-profile'));
const PlatformConfig = lazy(
  () => import('@/features/notification/pages/platform-config')
);
const PlatformBasicConfig = lazy(
  () => import('@/features/notification/pages/platform-basic-config')
);
const IntelligentMatchingPage = lazy(() =>
  import('@/features/matching/pages/intelligent-matching').then((m) => ({
    default: m.IntelligentMatchingPage,
  }))
);
const AuditLogPage = lazy(() => import('@/features/audit/pages/audit-log'));
const SystemSettingsPage = lazy(
  () => import('@/features/system-settings/pages/system-settings')
);
const RecruitmentPipelinePage = lazy(() =>
  import('@/features/recruitment/pages/recruitment-pipeline').then((m) => ({
    default: m.RecruitmentPipelinePage,
  }))
);
const WorkbenchPage = lazy(
  () => import('@/features/workbench/pages/workbench-page')
);
const DashboardPage = lazy(
  () => import('@/features/dashboard/pages/dashboard-page')
);
const InternalReferralManagementPage = lazy(
  () =>
    import('@/features/internal-referral/pages/internal-referral-management')
);
const InternalReferralJobDetailPage = lazy(
  () =>
    import('@/features/internal-referral/pages/internal-referral-job-detail')
);
const InterviewManagement = lazy(() =>
  import('@/features/interview/pages/interview-management').then((m) => ({
    default: m.InterviewManagement,
  }))
);
const OfferManagement = lazy(() =>
  import('@/features/offer/pages/offer-management').then((m) => ({
    default: m.OfferManagement,
  }))
);

// 路由级懒加载 - 认证页面
const LoginPage = lazy(() => import('@/features/auth/pages/login'));
const AdminLoginPage = lazy(() => import('@/features/auth/pages/admin-login'));
const RegisterPage = lazy(() => import('@/features/auth/pages/register'));
const OAuthCallbackPage = lazy(
  () => import('@/features/auth/pages/oauth-callback')
);
const LandingPage = lazy(() => import('@/features/landing/pages/landing-page'));

// 路由级懒加载 - 管理员页面
const AdminOrganizationManagementPage = lazy(
  () => import('@/features/admin/pages/admin-organization-management')
);
const AdminUserManagementPage = lazy(
  () => import('@/features/admin/pages/admin-user-management')
);
const AdminBackendManagementPage = lazy(
  () => import('@/features/admin/pages/admin-backend-management')
);
const AdminPermissionManagementPage = lazy(
  () => import('@/features/admin/pages/admin-permission-management')
);
const AdminAuthenticationManagementPage = lazy(
  () => import('@/features/admin/pages/admin-authentication-management')
);
const AdminModelManagementPage = lazy(
  () => import('@/features/admin/pages/admin-model-management')
);
const AdminPluginManagementPage = lazy(
  () => import('@/features/admin/pages/admin-plugin-management')
);
const AdminApiManagementPage = lazy(
  () => import('@/features/admin/pages/admin-api-management')
);
const AdminUniversityManagementPage = lazy(
  () => import('@/features/admin/pages/admin-university-management')
);
const AdminAdministrativeDivisionPage = lazy(
  () => import('@/features/admin/pages/admin-administrative-division')
);

// 占位页面（轻量，可以同步加载）
import { PlaceholderPage } from '@/components/common/placeholder-page';

// 页面加载组件
function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">加载中...</span>
      </div>
    </div>
  );
}

function App() {
  const appMode = (import.meta.env.VITE_APP_MODE || 'software').toLowerCase();
  const isSaasMode = appMode === 'saas';

  return (
    <ErrorBoundary>
      <TooltipProvider>
        <Router>
          <ToastContainer />
          <Routes>
            {/* 独立的前台认证页面 */}
            <Route
              path="/login"
              element={
                <AuthProvider>
                  <Suspense fallback={<PageLoading />}>
                    <LoginPage />
                  </Suspense>
                </AuthProvider>
              }
            />
            <Route
              path="/register"
              element={
                <AuthProvider>
                  <Suspense fallback={<PageLoading />}>
                    <RegisterPage />
                  </Suspense>
                </AuthProvider>
              }
            />
            <Route
              path="/oauth/callback"
              element={
                <AuthProvider>
                  <Suspense fallback={<PageLoading />}>
                    <OAuthCallbackPage />
                  </Suspense>
                </AuthProvider>
              }
            />
            {/* 根路径按模式决定默认入口 */}
            <Route
              path="/"
              element={
                isSaasMode ? (
                  <Suspense fallback={<PageLoading />}>
                    <LandingPage />
                  </Suspense>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            {/* 兼容历史/手动输入的落地页路径 */}
            <Route
              path="/LandingPage"
              element={
                isSaasMode ? (
                  <Suspense fallback={<PageLoading />}>
                    <LandingPage />
                  </Suspense>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />

            {/* 前台主应用路由 */}
            <Route
              element={
                <AuthProvider>
                  <MainLayout />
                </AuthProvider>
              }
            >
              <Route
                path="dashboard"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <DashboardPage />
                  </Suspense>
                }
              />
              <Route
                path="workbench"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <WorkbenchPage />
                  </Suspense>
                }
              />
              <Route
                path="internal-referral"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <InternalReferralManagementPage />
                  </Suspense>
                }
              />
              <Route
                path="internal-referral/:id"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <InternalReferralJobDetailPage />
                  </Suspense>
                }
              />
              <Route
                path="recruitment-pipeline"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <RecruitmentPipelinePage />
                  </Suspense>
                }
              />
              <Route
                path="resume-management"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ResumeManagementPage />
                  </Suspense>
                }
              />
              <Route
                path="resume-management/:resumeId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <ResumeManagementPage />
                  </Suspense>
                }
              />
              <Route
                path="job-profile"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <JobProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="job-profile/:jobId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <JobProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="job-profile/:jobId/edit"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <JobProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="platform-config"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <PlatformConfig />
                  </Suspense>
                }
              />
              <Route
                path="platform-basic-config"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <PlatformBasicConfig />
                  </Suspense>
                }
              />
              <Route
                path="matching"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <IntelligentMatchingPage />
                  </Suspense>
                }
              />
              <Route
                path="intelligent-matching"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <IntelligentMatchingPage />
                  </Suspense>
                }
              />
              <Route
                path="intelligent-matching/:taskId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <IntelligentMatchingPage />
                  </Suspense>
                }
              />
              <Route
                path="position-management"
                element={
                  <PlaceholderPage
                    title="岗位管理"
                    description="管理和发布招聘岗位"
                  />
                }
              />
              <Route
                path="candidate"
                element={
                  <PlaceholderPage
                    title="候选人"
                    description="管理候选人信息"
                  />
                }
              />
              <Route
                path="interview-management"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <InterviewManagement />
                  </Suspense>
                }
              />
              <Route
                path="interview-management/:taskId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <InterviewManagement />
                  </Suspense>
                }
              />
              <Route
                path="offer-management"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <OfferManagement />
                  </Suspense>
                }
              />
              <Route
                path="offer-management/:offerId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <OfferManagement />
                  </Suspense>
                }
              />
              <Route
                path="offer-management/:offerId/edit"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <OfferManagement />
                  </Suspense>
                }
              />
              <Route
                path="data-analysis"
                element={
                  <PlaceholderPage
                    title="数据分析"
                    description="查看招聘数据分析报告"
                  />
                }
              />
              <Route
                path="system-settings"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <SystemSettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="user-management"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <SystemSettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="operation-log"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AuditLogPage />
                  </Suspense>
                }
              />
              <Route
                path="no-permission"
                element={
                  <PlaceholderPage
                    title="暂无功能权限"
                    description="当前账号未分配可访问模块，请联系管理员配置角色权限"
                  />
                }
              />
              <Route
                path="permission-management"
                element={
                  <PlaceholderPage
                    title="权限管理"
                    description="配置用户权限"
                  />
                }
              />
            </Route>

            {/* 管理员后台路由 - 使用单一的AdminAuthProvider实例以保持认证状态 */}
            <Route
              path="/admin/*"
              element={
                <AdminAuthProvider>
                  <AdminRoutes />
                </AdminAuthProvider>
              }
            />
          </Routes>
        </Router>
      </TooltipProvider>
    </ErrorBoundary>
  );
}

// 管理员后台路由组件 - 避免AdminAuthProvider被重新创建
function AdminRoutes() {
  return (
    <Routes>
      <Route
        index
        element={<Navigate to="/admin/organization-management" replace />}
      />
      <Route
        path="login"
        element={
          <Suspense fallback={<PageLoading />}>
            <AdminLoginPage />
          </Suspense>
        }
      />
      <Route path="organization-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminOrganizationManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="user-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminUserManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="backend-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminBackendManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="remove-user-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminUserManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="authentication-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminAuthenticationManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="plugin-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminPluginManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="api-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminApiManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="model-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminModelManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="university-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminUniversityManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="administrative-division" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminAdministrativeDivisionPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="permission-management" element={<AdminLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <AdminPermissionManagementPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path="*"
        element={<Navigate to="/admin/organization-management" replace />}
      />
    </Routes>
  );
}

export default App;
